import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useEvidenceStore } from '../store/evidenceStore'
import SecureFileViewer from '../components/SecureFileViewer'
import { 
  BarChart3, 
  FileText, 
  Users, 
  Filter, 
  Download, 
  Calendar,
  Eye
} from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const ViewerDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [filters, setFilters] = useState({
    programa: '',
    dimension: '',
    criterio: '',
    fechaCarga: '',
    año: ''
  })
  const [viewerFile, setViewerFile] = useState(null)
  const [showViewer, setShowViewer] = useState(false)
  
  const { user, token } = useAuthStore()
  const { 
    evidences, 
    formStructure, 
    fetchAllEvidences, 
    fetchFormStructure,
    isLoading, 
    error 
  } = useEvidenceStore()

  useEffect(() => {
    fetchAllEvidences(token, filters)
    if (!formStructure) {
      fetchFormStructure()
    }
  }, [token, fetchAllEvidences, formStructure, fetchFormStructure])

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value }
    if (key === 'dimension') {
      newFilters.criterio = '' // Reset criterio when dimension changes
    }
    setFilters(newFilters)
  }

  const applyFilters = () => {
    fetchAllEvidences(token, filters)
  }

  const clearFilters = () => {
    const emptyFilters = {
      programa: '',
      dimension: '',
      criterio: '',
      fechaCarga: '',
      año: ''
    }
    setFilters(emptyFilters)
    fetchAllEvidences(token, emptyFilters)
  }

  // Filtrar evidencias localmente
  const filteredEvidences = evidences.filter(evidence => {
    // Filtro por programa
    if (filters.programa && evidence.programa !== filters.programa) {
      return false
    }
    
    // Filtro por dimensión
    if (filters.dimension && evidence.dimension !== filters.dimension) {
      return false
    }
    
    // Filtro por criterio
    if (filters.criterio && evidence.criterio !== filters.criterio) {
      return false
    }
    
    // Filtro por año (solo usar el campo año explícito)
    if (filters.año) {
      console.log(`Filtro año: ${filters.año} vs evidencia año: ${evidence.año} (${typeof evidence.año})`)
      if (!evidence.año || evidence.año.toString() !== filters.año.toString()) {
        console.log(`Evidencia ${evidence.nombre_archivo} filtrada por año`)
        return false
      }
    }
    
    // Filtro por fecha de carga
    if (filters.fechaCarga && evidence.fecha_hora) {
      const evidenceDate = new Date(evidence.fecha_hora).toISOString().split('T')[0]
      if (evidenceDate !== filters.fechaCarga) {
        return false
      }
    }
    
    return true
  })

  // Estadísticas (usando evidencias filtradas)
  const stats = {
    totalEvidences: filteredEvidences.length,
    totalPrograms: [...new Set(filteredEvidences.map(e => e.programa))].length,
    evidencesByDimension: filteredEvidences.reduce((acc, evidence) => {
      acc[evidence.dimension] = (acc[evidence.dimension] || 0) + 1
      return acc
    }, {}),
    recentUploads: filteredEvidences.filter(e => {
      const uploadDate = new Date(e.fecha_hora)
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      return uploadDate > weekAgo
    }).length
  }

  const programs = [...new Set(evidences.map(e => e.programa))].sort()
  const criterios = filters.dimension && formStructure 
    ? (formStructure[filters.dimension] || []).map(c => 
        typeof c === 'object' ? c.nombre : c
      ) 
    : []

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'evidences', label: 'Todas las Evidencias', icon: FileText },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
          <Eye className="h-8 w-8 text-blue-600" />
          <span>Panel de Visualización</span>
        </h1>
        <p className="text-gray-600">Vista de solo lectura del sistema de evidencias - {user?.correo}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Evidencias</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalEvidences}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Programas Activos</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalPrograms}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Subidas Recientes</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.recentUploads}</p>
                  <p className="text-xs text-gray-500">Última semana</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Dimensiones</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {Object.keys(stats.evidencesByDimension).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Evidencias por Dimensión */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Distribución por Dimensión
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.evidencesByDimension).map(([dimension, count]) => (
                <div key={dimension} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{dimension}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ 
                          width: `${(count / stats.totalEvidences) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Evidences Tab */}
      {activeTab === 'evidences' && (
        <div className="space-y-6">
          {/* Filtros */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <Filter className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Programa
                </label>
                <select
                  value={filters.programa}
                  onChange={(e) => handleFilterChange('programa', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todos los programas</option>
                  {programs.map(programa => (
                    <option key={programa} value={programa}>{programa}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensión
                </label>
                <select
                  value={filters.dimension}
                  onChange={(e) => handleFilterChange('dimension', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todas las dimensiones</option>
                  {formStructure && Object.keys(formStructure).map(dimension => (
                    <option key={dimension} value={dimension}>{dimension}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Criterio
                </label>
                <select
                  value={filters.criterio}
                  onChange={(e) => handleFilterChange('criterio', e.target.value)}
                  className="input-field"
                  disabled={!filters.dimension}
                >
                  <option value="">Todos los criterios</option>
                  {criterios.map(criterio => (
                    <option key={criterio} value={criterio}>{criterio}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de Carga
                </label>
                <input
                  type="date"
                  value={filters.fechaCarga}
                  onChange={(e) => handleFilterChange('fechaCarga', e.target.value)}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Año
                </label>
                <select
                  value={filters.año}
                  onChange={(e) => handleFilterChange('año', e.target.value)}
                  className="input-field"
                >
                  <option value="">Todos los años</option>
                  {Array.from({ length: 11 }, (_, i) => 2022 + i).map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-4">
              <button onClick={applyFilters} className="btn-primary">
                Aplicar Filtros
              </button>
              <button onClick={clearFilters} className="btn-secondary">
                Limpiar Filtros
              </button>
            </div>
          </div>

          {/* Lista de evidencias */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Evidencias ({filteredEvidences.length})
              </h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-sm text-blue-800 font-medium">
                  👁️ Modo Solo Lectura
                </span>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando evidencias...</p>
              </div>
            ) : filteredEvidences.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron evidencias</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Archivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Programa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dimensión
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Criterio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ver
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEvidences.map((evidence) => (
                      <tr key={evidence.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 text-gray-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {evidence.nombre_archivo}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {evidence.programa}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {evidence.dimension}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {evidence.criterio}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(new Date(evidence.fecha_hora), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {evidence.url_archivo ? (
                            <button
                              onClick={() => {
                                setViewerFile({
                                  url: evidence.url_archivo,
                                  name: evidence.nombre_archivo
                                })
                                setShowViewer(true)
                              }}
                              className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md transition-colors duration-200"
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span>Ver archivo</span>
                            </button>
                          ) : (
                            <span className="inline-flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-500 rounded-md">
                              <FileText className="h-4 w-4" />
                              <span>Sin archivo</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Visualizador de archivos */}
      <SecureFileViewer
        fileName={viewerFile}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false)
          setViewerFile(null)
        }}
      />
    </div>
  )
}

export default ViewerDashboard